import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useSelector } from "react-redux";
import { Box } from "@material-ui/core";
import {
  GetAllCompanyFinancesByPropertyId,
  DeleteCompanyFinance,
} from "../../../../../../Services";
import { financeTableHeaderData } from "./financeTableHeaderData";
import {
  BasicTable,
  TableSkeleton,
  DeleteConfirmationDialog,
  TableEditDeleteActions,
  NoDataFound,
  CustomTableHeader,
  CustomPagination,
} from "../../../../../../Components";
import { AddFinancePlanDialog } from "./UI";
import { useVerticalNav } from "../../../../../../Contexts/VerticalNavContext";

//  styles
import useStyles from "./styles";

const FinanceCompany = forwardRef(({ propretyId }, ref) => {
  const styles = useStyles();
  const [isLoading, setIsLoading] = useState(true);
  const [activeItem, setActiveItem] = useState({});
  const [isOpenDeleteConfirmation, setIsOpenDeleteConfirmation] =
    useState(false);
  const [isOpenAddFinancePlanOpen, setIsOpenAddFinancePlanOpen] =
    useState(false);
  const [financeCompanyData, setFinanceCompanyData] = useState({
    result: [],
    totalCount: 0,
  });

  const [pagination, setPagination] = useState({
    pageSize: 25,
    pageIndex: 1,
  });

  const [inputValue, setInputValue] = useState(pagination.pageIndex);

  const { setAlertBoxContent } = useVerticalNav();
  const { isDarkMode } = useSelector((state) => state.theme);

  const handleGetFinanceCompanyData = async () => {
    setIsLoading(true);
    try {
      const res = await GetAllCompanyFinancesByPropertyId(
        pagination.pageIndex,
        pagination.pageSize,
        propretyId
      );
      if (!(res && res.status && res.status !== 200)) {
        setFinanceCompanyData({
          result: res.result,
          totalCount: (res && res.totalCount) || 0,
        });
        setIsLoading(false);
      }
    } catch (error) {
      setFinanceCompanyData({
        result: [],
        totalCount: 0,
      });
      console.error("Error fetching finance company data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteFinanceCompany = async () => {
    setIsLoading(true);
    try {
      const res = await DeleteCompanyFinance(
        activeItem && activeItem.companyFinanceId
      );
      if (!(res && res.status && res.status !== 200)) {
        setAlertBoxContent({
          display: true,
          variant: "success",
          title: "finance company deleted successfully",
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
        handleReload();
      }
    } catch (error) {
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: "finance company delete Failed",
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
    } finally {
      setIsLoading(false);
      restConfirmationState();
    }
  };

  const handleReload = () => {
    handleGetFinanceCompanyData();
  };
  const restConfirmationState = () => {
    setActiveItem({});
    setIsOpenDeleteConfirmation(false);
  };

  const addFinancePlanModelHandler = () => {
    setActiveItem({});
    setIsOpenAddFinancePlanOpen(true);
  };

  useImperativeHandle(ref, () => ({
    addFinancePlanModelHandler,
  }));

  useEffect(() => {
    if (propretyId) {
      handleGetFinanceCompanyData();
    }
  }, [propretyId, pagination]);

  return (
    <Box>
      {isLoading ? (
        <TableSkeleton rows={12} />
      ) : financeCompanyData?.result?.length > 0 ? (
        <BasicTable
          rowHeight={84}
          setActiveItem={setActiveItem}
          setActiveItemOnRowClick={(item) => {
            setActiveItem(item);
          }}
          rowsData={
            financeCompanyData?.result.map((item) => {
              return {
                ...item,
                id: item.companyFinanceId,
              };
            }) || []
          }
          columns={financeTableHeaderData(isDarkMode)?.map((column) => ({
            ...column,
            renderHeader: (params) => (
              <CustomTableHeader
                params={params}
                title={column.headerName}
                isHiddenFilter={true}
              />
            ),
          }))}
          tableActions={({ anchorPosition }) => {
            if (!activeItem || Object.keys(activeItem).length === 0)
              return null;
            return (
              <TableEditDeleteActions
                anchorPosition={anchorPosition}
                onDelete={() => setIsOpenDeleteConfirmation(true)}
              />
            );
          }}
        />
      ) : (
        <NoDataFound title="finance companies" />
      )}
      <CustomPagination
        totalItems={financeCompanyData?.totalCount}
        itemsPerPage={pagination.pageSize}
        currentPage={pagination.pageIndex}
        inputValue={inputValue}
        setInputValue={setInputValue}
        onPageChange={(page) =>
          setPagination((prev) => ({ ...prev, pageIndex: page }))
        }
        onItemsPerPageChange={(items) =>
          setPagination((prev) => ({ ...prev, pageSize: items }))
        }
        isLoading={isLoading}
      />
      {Boolean(activeItem) && isOpenDeleteConfirmation && (
        <DeleteConfirmationDialog
          title={"Finance company will be deleted"}
          subTitle={
            "Are you sure you want to delete this finance company? This action cannot be undone"
          }
          onClose={() => {
            restConfirmationState();
          }}
          onConfirm={() => {
            handleDeleteFinanceCompany();
          }}
        />
      )}
      {isOpenAddFinancePlanOpen && (
        <AddFinancePlanDialog
          onClose={() => {
            setIsOpenAddFinancePlanOpen(false);
            setActiveItem({});
          }}
          propretyId={propretyId}
          reloadData={handleReload}
        />
      )}
    </Box>
  );
});

export default FinanceCompany;
